import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API, useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { 
  Mic, 
  Loader2, 
  AlertCircle,
  Play,
  Target,
  Building2,
  BarChart3,
  CheckCircle2,
  ExternalLink
} from "lucide-react";

export default function EmbeddedScenario() {
  const { embedId } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  
  const [embedConfig, setEmbedConfig] = useState(null);
  const [scenario, setScenario] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [completedSession, setCompletedSession] = useState(null);

  // Fetch embed config (public, no auth needed)
  useEffect(() => {
    const fetchConfig = async () => {
      try {
        const res = await fetch(`${API}/embed/${embedId}/config`);
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || "Embed not found");
        }
        const data = await res.json();
        setEmbedConfig(data);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchConfig();
  }, [embedId]);

  // Fetch full scenario when user is authenticated
  useEffect(() => {
    if (!user || !embedConfig) return;
    
    const fetchScenario = async () => {
      try {
        const res = await fetch(`${API}/embed/${embedId}/scenario`, {
          credentials: "include"
        });
        if (!res.ok) {
          const err = await res.json();
          throw new Error(err.detail || "Failed to load scenario");
        }
        const data = await res.json();
        setScenario(data.scenario);
      } catch (err) {
        console.error("Failed to fetch scenario:", err);
      }
    };
    
    fetchScenario();
  }, [user, embedConfig, embedId]);

  // Check for completed sessions
  useEffect(() => {
    if (!user) return;
    
    const checkCompletedSession = async () => {
      try {
        const res = await fetch(`${API}/sessions`, { credentials: "include" });
        if (res.ok) {
          const sessions = await res.json();
          // Find the most recent completed session for this embed
          const embedSession = sessions.find(s => 
            s.embed_id === embedId && s.status === "completed"
          );
          if (embedSession) {
            setCompletedSession(embedSession);
          }
        }
      } catch (err) {
        console.error("Failed to check sessions:", err);
      }
    };
    
    checkCompletedSession();
  }, [user, embedId]);

  const handleLogin = () => {
    // Redirect to Google OAuth with return URL
    const returnUrl = encodeURIComponent(window.location.href);
    window.location.href = `${API}/auth/login?return_url=${returnUrl}`;
  };

  const handleStartRoleplay = async () => {
    if (!scenario) return;
    
    setStarting(true);
    try {
      // Navigate to active roleplay with embed context
      navigate(`/embed/${embedId}/active`, {
        state: {
          scenario: scenario,
          scenarioId: scenario.scenario_id || scenario.template_id,
          embedId: embedId,
        }
      });
    } catch (err) {
      toast.error("Failed to start roleplay");
      setStarting(false);
    }
  };

  const handleViewResults = () => {
    if (completedSession) {
      navigate(`/embed/${embedId}/results/${completedSession.session_id}`);
    }
  };

  const handleTryAgain = () => {
    setCompletedSession(null);
  };

  const getDifficultyColor = (difficulty) => {
    switch (difficulty) {
      case "easy": return "bg-green-500/10 text-green-500";
      case "medium": return "bg-yellow-500/10 text-yellow-500";
      case "hard": return "bg-orange-500/10 text-orange-500";
      case "extreme": return "bg-red-500/10 text-red-500";
      default: return "bg-gray-500/10 text-gray-500";
    }
  };

  if (loading || authLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            <AlertCircle className="w-12 h-12 text-destructive mx-auto mb-4" />
            <h2 className="text-xl font-semibold mb-2">Scenario Not Found</h2>
            <p className="text-muted-foreground">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background" data-testid="embedded-scenario-page">
      {/* Simple header */}
      <header className="border-b border-border bg-card/50 backdrop-blur-sm">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-primary/10 flex items-center justify-center">
              <Mic className="w-4 h-4 text-primary" />
            </div>
            <span className="font-semibold">Sales Training</span>
          </div>
          {user && (
            <div className="text-sm text-muted-foreground">
              {user.name}
            </div>
          )}
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Scenario Card */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex items-start justify-between">
              <div>
                <CardTitle className="text-2xl">{embedConfig?.scenario_name || "Sales Roleplay"}</CardTitle>
                <CardDescription className="mt-2">
                  {embedConfig?.name}
                </CardDescription>
              </div>
              {embedConfig?.difficulty && (
                <Badge className={getDifficultyColor(embedConfig.difficulty)}>
                  {embedConfig.difficulty}
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-6">
              <div className="flex items-center gap-2 text-sm">
                <Target className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Type:</span>
                <span className="font-medium capitalize">
                  {embedConfig?.scenario_type?.replace(/_/g, " ")}
                </span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Building2 className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Industry:</span>
                <span className="font-medium capitalize">{embedConfig?.industry}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <BarChart3 className="w-4 h-4 text-muted-foreground" />
                <span className="text-muted-foreground">Difficulty:</span>
                <span className="font-medium capitalize">{embedConfig?.difficulty}</span>
              </div>
            </div>

            {/* Not logged in */}
            {!user && (
              <div className="text-center py-8 border-t border-border">
                <h3 className="text-lg font-semibold mb-2">Ready to Practice?</h3>
                <p className="text-muted-foreground mb-6">
                  Sign in with your Google account to start the roleplay session.
                </p>
                <Button size="lg" onClick={handleLogin} data-testid="login-btn">
                  Sign In with Google
                </Button>
              </div>
            )}

            {/* Logged in - Show completed or start */}
            {user && completedSession && (
              <div className="text-center py-8 border-t border-border">
                <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto mb-4" />
                <h3 className="text-lg font-semibold mb-2">Session Completed!</h3>
                <p className="text-muted-foreground mb-2">
                  You completed this scenario with a score of{" "}
                  <span className="font-bold text-primary">{completedSession.score || "N/A"}</span>
                </p>
                <div className="flex items-center justify-center gap-3 mt-6">
                  <Button variant="outline" onClick={handleTryAgain} data-testid="try-again-btn">
                    Try Again
                  </Button>
                  <Button onClick={handleViewResults} data-testid="view-results-btn">
                    View Results
                    <ExternalLink className="w-4 h-4 ml-2" />
                  </Button>
                </div>
              </div>
            )}

            {user && !completedSession && (
              <div className="text-center py-8 border-t border-border">
                <h3 className="text-lg font-semibold mb-2">Ready to Start</h3>
                <p className="text-muted-foreground mb-6">
                  Click the button below to begin your voice roleplay session.
                </p>
                <Button 
                  size="lg" 
                  onClick={handleStartRoleplay}
                  disabled={starting || !scenario}
                  data-testid="start-roleplay-btn"
                >
                  {starting ? (
                    <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                  ) : (
                    <Play className="w-5 h-5 mr-2" />
                  )}
                  Start Roleplay
                </Button>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">How It Works</CardTitle>
          </CardHeader>
          <CardContent>
            <ol className="space-y-3 text-sm text-muted-foreground">
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">1</span>
                <span>Click "Start Roleplay" to begin a voice conversation with an AI client.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">2</span>
                <span>Practice your sales pitch naturally - the AI will respond like a real prospect.</span>
              </li>
              <li className="flex gap-3">
                <span className="flex-shrink-0 w-6 h-6 rounded-full bg-primary/10 text-primary flex items-center justify-center text-xs font-medium">3</span>
                <span>End the session when ready to receive detailed AI feedback and scoring.</span>
              </li>
            </ol>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
